import { sleep } from '@takeshape/util';
import { useMutation, useQuery } from '@tanstack/react-query';
import { showMessage } from 'app/messages/actions';
import { submitForm } from 'app/schema-form/actions';
import type { ErrorList, OnSubmit, Validator } from 'app/schema-form/types';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { type EvalDataSource, useAgentEvalStore } from '../../agent-eval-store.js';
import { rowData } from '../agent-evals/agent-evals-hook.js';
import { EVAL_FORM_NAME, type EvalFormState } from './eval-form.js';

export const DEFAULT_PROMPT = `## INSTRUCTIONS

You are an agent evaluator. Please review the transcript after the subheading "TRANSCRIPT" given the following crieria:

* The agent should be courteous and professional.

## TRANSCRIPT

{{transcript}}`;

export const useAgentEvalEditRun = () => {
  const dispatch = useDispatch();
  const runEditOpen = useAgentEvalStore((state) => state.runEditOpen);
  const closeModal = useAgentEvalStore((state) => state.closeModal);
  const runEditCreateMode = useAgentEvalStore((state) => state.runEditCreateMode);
  const runEditEvalId = useAgentEvalStore((state) => state.runEditEvalId);
  const dataSource = useAgentEvalStore((state) => state.dataSource);
  const setDataSource = useAgentEvalStore((state) => state.setDataSource);
  const dataSourceOptions = useAgentEvalStore((state) => state.dataSourceOptions);
  const setDataSourceOptions = useAgentEvalStore((state) => state.setDataSourceOptions);
  const sessionId = useAgentEvalStore((state) => state.sessionId);
  const setSessionId = useAgentEvalStore((state) => state.setSessionId);

  const { data: allEvals } = useQuery({
    queryKey: ['evalsNames'],
    queryFn: async () => {
      const rows = rowData;
      await sleep(500);
      return {
        nodes: rows,
        pageInfo: {
          endCursor: undefined,
          hasNextPage: false
        }
      };
    },
    enabled: runEditOpen
  });

  const validator: Validator<EvalFormState> = useCallback(
    (values) => {
      const result: ErrorList = {};
      if (
        allEvals?.nodes.find(
          (node) => node.id !== runEditEvalId && node.name.toLowerCase() === values.name.toLowerCase()
        )
      ) {
        result.name = 'This name is already taken.';
      }
      return result;
    },
    [allEvals?.nodes.find, runEditEvalId]
  );

  const { data: evalToEdit } = useQuery({
    queryKey: ['evalId', runEditEvalId],
    queryFn: async () => {
      await sleep(500);
      return {
        ...rowData[0],
        prompt: DEFAULT_PROMPT
      };
    },
    keepPreviousData: true,
    refetchOnMount: 'always',
    enabled: runEditOpen
  });

  const initialData = useMemo(() => {
    if (runEditCreateMode) {
      return {
        name: 'New Eval',
        prompt: DEFAULT_PROMPT
      };
    }
    return evalToEdit;
  }, [runEditCreateMode, evalToEdit]);

  const { mutate: save, isLoading: saving } = useMutation({
    mutationFn: async (data: EvalFormState) => {
      // TODO
      await sleep(500);
      return data;
    },
    onSuccess(data) {
      dispatch(showMessage('info', `Saved changes for "${data.name}"`, 5000));
    },
    onError() {
      dispatch(showMessage('error', `Error saving changes.`));
    }
  });

  const submitCallback: OnSubmit<EvalFormState> = useCallback(
    (values) => {
      void save(values.data);
    },
    [save]
  );

  const handleSubmit = useCallback(() => {
    dispatch(submitForm(EVAL_FORM_NAME, submitCallback));
  }, [dispatch, submitCallback]);

  const {
    data: evalResults,
    mutate: runEval,
    isLoading: isRunning,
    reset: resetEval
  } = useMutation({
    mutationFn: async () => {
      // TODO
      await sleep(500);
      return {
        result: 'Mock Eval Result'
      };
    },
    onError() {
      dispatch(showMessage('error', `Error running eval.`));
    }
  });

  useQuery({
    queryKey: ['evalDataSources', runEditEvalId],
    queryFn: async () => {
      await sleep(500);
      const dataSources: EvalDataSource[] = [
        { type: 'Transcript', id: 'transcript-1', name: 'transcript 1' },
        { type: 'Transcript', id: 'transcript-2', name: 'transcript 2' },
        { type: 'Transcript Tag', id: 'tag-1', name: 'tag 1' },
        { type: 'Session ID', id: 'session' }
      ];
      setDataSourceOptions(dataSources);
    },
    enabled: runEditOpen
  });

  return {
    loading: !runEditCreateMode && !evalToEdit,
    saving,
    initialData,
    runEditOpen,
    closeModal,
    handleSubmit,
    dataSourceOptions,
    dataSource,
    setDataSource,
    resetEval,
    runEval,
    isRunning,
    evalResults,
    validator,
    sessionId,
    setSessionId
  };
};

export type AgentEvalRunEditProps = ReturnType<typeof useAgentEvalEditRun>;
